import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useBluelagoon } from "@bluelagoon";
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@shadcn-in-nx/ui/table";
import { Input } from "@shadcn-in-nx/ui/input";
import { Button } from "@shadcn-in-nx/ui/button";
import { Skeleton } from "@shadcn-in-nx/ui/skeleton";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@shadcn-in-nx/ui/select";
import {
    Popover, PopoverContent, PopoverTrigger,
} from "@shadcn-in-nx/ui/popover";
import {
    Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "@shadcn-in-nx/ui/command";
import {
    Search, ChevronLeft, ChevronRight, CheckCircle,
    ThumbsUp, ThumbsDown, Meh, ChevronsUpDown, Check, Eye, Upload,
} from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { cn } from "@shadcn-in-nx/utils";
import { useTranslation } from "react-i18next";

const getCreditColor = (credits) => {
    if (credits >= 50) return "text-red-600 bg-red-50 font-semibold";
    if (credits >= 25) return "text-yellow-600 bg-yellow-50 font-medium";
    return "text-gray-600";
};

export default function ChatMessagesPanel() {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [searchParams, setSearchParams] = useSearchParams();
    const [searchQuery, setSearchQuery] = useState(searchParams.get("q") || "");
    const [selectedWorkflow, setSelectedWorkflow] = useState(searchParams.get("workflow") || "all");
    const [selectedCompany, setSelectedCompany] = useState(searchParams.get("company") || "all");
    const [selectedRating, setSelectedRating] = useState(searchParams.get("rating") || "all");
    const [selectedToolStatus, setSelectedToolStatus] = useState(searchParams.get("toolStatus") || "all");
    const [selectedToolName, setSelectedToolName] = useState(searchParams.get("toolName") || "all");
    const [sortBy, setSortBy] = useState(searchParams.get("sortBy") || "updatedAt");
    const [currentPage, setCurrentPage] = useState(Number(searchParams.get("page")) || 1);
    const [pageSize] = useState(10);
    const refTimeout = useRef(null);
    const searchDebounceRef = useRef(null);
    const [debouncedSearch, setDebouncedSearch] = useState(searchQuery);

    const [companySearch, setCompanySearch] = useState("");
    const [workflowSearch, setWorkflowSearch] = useState("");
    const [companyOpen, setCompanyOpen] = useState(false);
    const [workflowOpen, setWorkflowOpen] = useState(false);
    const companySearchTimeout = useRef(null);
    const workflowSearchTimeout = useRef(null);
    const filterSearchMounted = useRef(false);

    // Sync filters to URL search params
    const updateParams = useCallback((overrides = {}) => {
        const vals = { q: searchQuery, workflow: selectedWorkflow, company: selectedCompany, rating: selectedRating, toolStatus: selectedToolStatus, toolName: selectedToolName, sortBy, page: String(currentPage), ...overrides };
        const next = new URLSearchParams();
        Object.entries(vals).forEach(([k, v]) => { if (v && v !== "all" && v !== "updatedAt" && !(k === "page" && v === "1")) next.set(k, v); });
        setSearchParams(next, { replace: true });
    }, [searchQuery, selectedWorkflow, selectedCompany, selectedRating, selectedToolStatus, selectedToolName, sortBy, currentPage, setSearchParams]);

    useEffect(() => { updateParams(); }, [selectedWorkflow, selectedCompany, selectedRating, selectedToolStatus, selectedToolName, sortBy, currentPage, debouncedSearch]);

    // Debounce the main search input
    useEffect(() => {
        if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
        searchDebounceRef.current = setTimeout(() => {
            setDebouncedSearch(searchQuery);
            updateParams({ q: searchQuery });
        }, 300);
        return () => clearTimeout(searchDebounceRef.current);
    }, [searchQuery]);

    const { call: getAllStepChats, response: data, isLoading, error } = useBluelagoon({
        method: "getAllStepChats",
    });

    const { call: getStepChatFilters, response: filtersData } = useBluelagoon({
        method: "getStepChatFilters",
        resetResponse: false,
    });

    useEffect(() => { getStepChatFilters({}); }, []);

    useEffect(() => {
        if (!filterSearchMounted.current) { filterSearchMounted.current = true; return; }
        if (companySearchTimeout.current) clearTimeout(companySearchTimeout.current);
        companySearchTimeout.current = setTimeout(() => {
            getStepChatFilters({
                companyId: selectedCompany !== "all" ? selectedCompany : undefined,
                companySearch: companySearch || undefined,
                workflowSearch: workflowSearch || undefined,
            });
        }, 300);
    }, [companySearch, workflowSearch]);

    useEffect(() => {
        if (filtersData?.isSuperAdmin && selectedCompany !== "all") {
            getStepChatFilters({ companyId: selectedCompany });
        }
    }, [selectedCompany]);

    useEffect(() => { fetchChats(); }, [debouncedSearch, selectedWorkflow, selectedCompany, selectedRating, selectedToolStatus, selectedToolName, sortBy, currentPage]);

    const fetchChats = () => {
        if (refTimeout.current) clearTimeout(refTimeout.current);
        const params = { limit: pageSize, offset: (currentPage - 1) * pageSize };
        if (debouncedSearch?.trim()) params.search = debouncedSearch.trim();
        if (selectedWorkflow !== "all") params.workflowId = selectedWorkflow;
        if (selectedCompany !== "all") params.companyId = selectedCompany;
        if (selectedRating !== "all") params.rating = selectedRating;
        if (selectedToolStatus === "failed") params.hasFailedToolCall = "true";
        if (selectedToolName !== "all") params.toolName = selectedToolName;
        if (sortBy && sortBy !== "updatedAt") params.sortBy = sortBy;
        getAllStepChats(params).catch(console.error);
    };

    const getRatingDisplay = (rating) => {
        if (!rating) return <span className="text-gray-400 text-xs">Unrated</span>;
        if (rating === 1) return <span className="inline-flex items-center gap-1 text-red-500 text-xs"><ThumbsDown className="w-3.5 h-3.5" />Bad</span>;
        if (rating === 2) return <span className="inline-flex items-center gap-1 text-yellow-500 text-xs"><Meh className="w-3.5 h-3.5" />Okay</span>;
        if (rating === 3) return <span className="inline-flex items-center gap-1 text-green-500 text-xs"><ThumbsUp className="w-3.5 h-3.5" />Great</span>;
        return null;
    };

    const totalPages = Math.ceil((data?.pagination?.total || 0) / pageSize);
    const selectedCompanyName = filtersData?.companies?.find(c => c.id === selectedCompany)?.name;
    const selectedWorkflowName = filtersData?.workflows?.find(w => w.id === selectedWorkflow)?.name;

    const { chatViews, lastViewedChatId } = useMemo(() => {
        try {
            return {
                chatViews: JSON.parse(localStorage.getItem("admin_chat_views") || "{}"),
                lastViewedChatId: localStorage.getItem("admin_last_viewed_chat"),
            };
        } catch (e) { return { chatViews: {}, lastViewedChatId: null }; }
    }, [data]);

    if (error) return <div className="p-4 text-red-500">Error: {error.message}</div>;

    return (
        <div className="space-y-4 p-2">
            <div className="flex items-center justify-between gap-2">
                <h2 className="font-semibold text-xl">Chat Messages</h2>
                <Button variant="outline" size="sm" onClick={() => navigate("/admin/chats/import")}>
                    <Upload className="w-4 h-4 mr-1" /> {t("adminChats.import.button")}
                </Button>
            </div>

            <div className="flex items-center gap-4 flex-wrap">
                <div className="relative flex-1 max-w-md">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <Input
                        placeholder="Search messages..."
                        value={searchQuery}
                        onChange={(e) => { setSearchQuery(e.target.value); if (currentPage !== 1) setCurrentPage(1); }}
                        className="pl-10"
                    />
                </div>

                {filtersData?.isSuperAdmin && (
                    <Popover open={companyOpen} onOpenChange={setCompanyOpen}>
                        <PopoverTrigger asChild>
                            <Button variant="outline" role="combobox" className="w-[200px] justify-between">
                                {selectedCompany === "all" ? "All Companies" : selectedCompanyName || "Select..."}
                                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                            </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-[200px] p-0">
                            <Command shouldFilter={false}>
                                <CommandInput placeholder="Search by name or email..." value={companySearch} onValueChange={setCompanySearch} />
                                <CommandList>
                                    <CommandEmpty>No company found.</CommandEmpty>
                                    <CommandGroup>
                                        <CommandItem value="all" onSelect={() => { setSelectedCompany("all"); setSelectedWorkflow("all"); setCurrentPage(1); setCompanyOpen(false); }}>
                                            <Check className={cn("mr-2 h-4 w-4", selectedCompany === "all" ? "opacity-100" : "opacity-0")} />
                                            All Companies
                                        </CommandItem>
                                        {filtersData?.companies?.map((c) => (
                                            <CommandItem key={c.id} value={c.name} onSelect={() => { setSelectedCompany(c.id); setSelectedWorkflow("all"); setCurrentPage(1); setCompanyOpen(false); }}>
                                                <Check className={cn("mr-2 h-4 w-4", selectedCompany === c.id ? "opacity-100" : "opacity-0")} />
                                                {c.name}
                                            </CommandItem>
                                        ))}
                                    </CommandGroup>
                                    {filtersData?.hasMoreCompanies && (
                                        <div className="p-2 text-xs text-muted-foreground text-center">Type to search more...</div>
                                    )}
                                </CommandList>
                            </Command>
                        </PopoverContent>
                    </Popover>
                )}

                <Popover open={workflowOpen} onOpenChange={setWorkflowOpen}>
                    <PopoverTrigger asChild>
                        <Button variant="outline" role="combobox" className="w-[200px] justify-between">
                            {selectedWorkflow === "all" ? "All Workflows" : selectedWorkflowName || "Select..."}
                            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[250px] p-0">
                        <Command shouldFilter={false}>
                            <CommandInput placeholder="Search workflow..." value={workflowSearch} onValueChange={setWorkflowSearch} />
                            <CommandList>
                                <CommandEmpty>No workflow found.</CommandEmpty>
                                <CommandGroup>
                                    <CommandItem value="all" onSelect={() => { setSelectedWorkflow("all"); setCurrentPage(1); setWorkflowOpen(false); }}>
                                        <Check className={cn("mr-2 h-4 w-4", selectedWorkflow === "all" ? "opacity-100" : "opacity-0")} />
                                        All Workflows
                                    </CommandItem>
                                    {filtersData?.workflows?.map((w) => (
                                        <CommandItem key={w.id} value={w.name} onSelect={() => { setSelectedWorkflow(w.id); setCurrentPage(1); setWorkflowOpen(false); }}>
                                            <Check className={cn("mr-2 h-4 w-4", selectedWorkflow === w.id ? "opacity-100" : "opacity-0")} />
                                            {filtersData?.isSuperAdmin && w.app?.company ? `${w.name} (${w.app.company.name})` : w.name}
                                        </CommandItem>
                                    ))}
                                </CommandGroup>
                                {filtersData?.hasMoreWorkflows && (
                                    <div className="p-2 text-xs text-muted-foreground text-center">Type to search more...</div>
                                )}
                            </CommandList>
                        </Command>
                    </PopoverContent>
                </Popover>

                <Select value={selectedRating} onValueChange={(v) => { setSelectedRating(v); setCurrentPage(1); }}>
                    <SelectTrigger className="w-[140px]"><SelectValue placeholder="All Ratings" /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All Ratings</SelectItem>
                        <SelectItem value="3">Great</SelectItem>
                        <SelectItem value="2">Okay</SelectItem>
                        <SelectItem value="1">Bad</SelectItem>
                        <SelectItem value="unrated">Unrated</SelectItem>
                    </SelectContent>
                </Select>

                <Select value={selectedToolStatus} onValueChange={(v) => { setSelectedToolStatus(v); setCurrentPage(1); }}>
                    <SelectTrigger className="w-[180px]"><SelectValue placeholder={t("adminChats.filters.toolStatusAll")} /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">{t("adminChats.filters.toolStatusAll")}</SelectItem>
                        <SelectItem value="failed">{t("adminChats.filters.hasFailedToolCall")}</SelectItem>
                    </SelectContent>
                </Select>

                <Select value={selectedToolName} onValueChange={(v) => { setSelectedToolName(v); setCurrentPage(1); }}>
                    <SelectTrigger className="w-[180px]"><SelectValue placeholder={t("adminChats.filters.toolNameAll")} /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">{t("adminChats.filters.toolNameAll")}</SelectItem>
                        {filtersData?.toolNames?.map((tn) => (
                            <SelectItem key={tn} value={tn}>{tn}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>

                <Select value={sortBy} onValueChange={(v) => { setSortBy(v); setCurrentPage(1); }}>
                    <SelectTrigger className="w-[200px]"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="updatedAt">{t("adminChats.sort.mostRecent")}</SelectItem>
                        <SelectItem value="failedToolCalls">{t("adminChats.sort.mostFailedToolCalls")}</SelectItem>
                    </SelectContent>
                </Select>

                {data?.pagination && (
                    <div className="text-sm text-gray-600">
                        {(currentPage - 1) * pageSize + 1}-{Math.min(currentPage * pageSize, data.pagination.total)} of {data.pagination.total}
                    </div>
                )}
            </div>

            <Table>
                <TableHeader>
                    <TableRow>
                        {filtersData?.isSuperAdmin && <TableHead>Company</TableHead>}
                        <TableHead>Workflow</TableHead>
                        <TableHead>Step</TableHead>
                        <TableHead>Rating</TableHead>
                        <TableHead>{t("adminChats.credits")}</TableHead>
                        <TableHead>{t("adminChats.failedToolCalls")}</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Last Updated</TableHead>
                        <TableHead className="w-[100px]">Actions</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {isLoading ? (
                        [...Array(5)].map((_, i) => (
                            <TableRow key={i}>
                                {filtersData?.isSuperAdmin && <TableCell><Skeleton className="h-4 w-24" /></TableCell>}
                                <TableCell><Skeleton className="h-4 w-28" /></TableCell>
                                <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                                <TableCell><Skeleton className="h-4 w-8" /></TableCell>
                                <TableCell><Skeleton className="h-4 w-12" /></TableCell>
                                <TableCell><Skeleton className="h-4 w-8" /></TableCell>
                                <TableCell><Skeleton className="h-4 w-16" /></TableCell>
                                <TableCell><Skeleton className="h-4 w-20" /></TableCell>
                                <TableCell><Skeleton className="h-4 w-14" /></TableCell>
                            </TableRow>
                        ))
                    ) : !data?.stepChats?.length ? (
                        <TableRow>
                            <TableCell colSpan={filtersData?.isSuperAdmin ? 9 : 8} className="text-center text-gray-500 py-8">
                                No conversations found
                            </TableCell>
                        </TableRow>
                    ) : (
                        data.stepChats.map((chat) => {
                            const isLastViewed = chat.id === lastViewedChatId;
                            const viewedAt = chatViews[chat.id];
                            return (
                                <TableRow key={chat.id} className={`cursor-pointer hover:bg-gray-50 ${isLastViewed ? "bg-blue-50/60 ring-1 ring-inset ring-blue-300" : viewedAt ? "border-l-4 border-l-blue-400" : ""}`} onClick={() => navigate(`/admin/chats/${chat.id}`)}>
                                    {filtersData?.isSuperAdmin && (
                                        <TableCell className="text-sm text-gray-600">{chat.workflow?.app?.company?.name || "Unknown"}</TableCell>
                                    )}
                                    <TableCell className="font-medium">{chat.workflow?.name || "Unknown"}</TableCell>
                                    <TableCell>{chat.workflowStep?.name || "Unknown"}</TableCell>
                                    <TableCell>{getRatingDisplay(chat.rating)}</TableCell>
                                    <TableCell>
                                        <span className={cn("inline-flex px-2 py-0.5 rounded text-xs", getCreditColor(chat.totalCredits || 0))}>
                                            {chat.totalCredits != null ? chat.totalCredits.toFixed(2) : "-"}
                                        </span>
                                    </TableCell>
                                    <TableCell>
                                        <span className={cn(
                                            "inline-flex px-2 py-0.5 rounded text-xs font-medium",
                                            chat.failedToolCallCount > 0 ? "bg-red-100 text-red-700" : "text-gray-400"
                                        )}>
                                            {chat.failedToolCallCount ?? 0}
                                        </span>
                                    </TableCell>
                                    <TableCell>
                                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs ${chat.isActive ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-600"}`}>
                                            {chat.isActive && <CheckCircle className="w-3 h-3" />}
                                            {chat.isActive ? "Active" : "Archived"}
                                        </span>
                                    </TableCell>
                                    <TableCell className="text-gray-500 text-sm">
                                        <div>{formatDistanceToNow(new Date(chat.updatedAt), { addSuffix: true })}</div>
                                        {viewedAt && (
                                            <div className="flex items-center gap-1 text-blue-600 text-xs mt-0.5">
                                                <Eye className="w-3 h-3" />
                                                {formatDistanceToNow(new Date(viewedAt), { addSuffix: true })}
                                            </div>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); navigate(`/admin/chats/${chat.id}`); }}>
                                            View
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            );
                        }))
                    }
                </TableBody>
            </Table>

            {totalPages > 1 && (
                <div className="flex items-center justify-between">
                    <div className="text-sm text-gray-600">Page {currentPage} of {totalPages}</div>
                    <div className="flex items-center gap-2">
                        <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => p - 1)} disabled={currentPage === 1}>
                            <ChevronLeft className="w-4 h-4" /> Previous
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => p + 1)} disabled={currentPage === totalPages}>
                            Next <ChevronRight className="w-4 h-4" />
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
}
